import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const OrganHealthResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [organHealthData, setOrganHealthData] = useState([]);
  const [latestAssessment, setLatestAssessment] = useState(null);

  const loadOrganHealthData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/organ-health/latest');
      setOrganHealthData(response.data.organHealth || []);
      setLatestAssessment(response.data.latestAssessment);
    } catch (error) {
      console.error('Failed to load organ health data:', error);
      // If no data, show state from location (from questionnaire submission)
      if (location.state?.results) {
        setLatestAssessment(location.state.results);
      }
    } finally {
      setLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    // Only load data if user is authenticated
    if (isAuthenticated) {
      loadOrganHealthData();
    } else {
      // If not authenticated, show state from location (from questionnaire submission)
      if (location.state?.results) {
        setLatestAssessment(location.state.results);
        // Try to extract organ health data from results
        if (location.state.results.predictions) {
          const organData = Object.entries(location.state.results.predictions).map(([organ, data]) => ({
            id: organ,
            organ_name: organ.charAt(0).toUpperCase() + organ.slice(1),
            health_score: data.final_score || 75,
            risk_level: data.risk_level || 'moderate',
            recommendations: data.recommendations || ['Maintain healthy habits'],
            prediction_data: data,
            created_at: new Date().toISOString()
          }));
          setOrganHealthData(organData);
        }
      }
    }
  }, [isAuthenticated, location.state, loadOrganHealthData]);

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (score >= 40) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'very_low': return 'text-green-700 bg-green-100';
      case 'low': return 'text-green-600 bg-green-50';
      case 'moderate': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'very_high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getOrganIcon = (organName) => {
    switch (organName.toLowerCase()) {
      case 'brain': return '🧠';
      case 'lung': return '🫁';
      case 'stomach': return '🍽️';
      case 'liver': return '🍷';
      case 'heart': return '❤️';
      case 'kidney': return '🫘';
      default: return '🏥';
    }
  };

  const getHealthMessage = (score) => {
    if (score >= 80) return 'Excellent Health';
    if (score >= 60) return 'Good Health';
    if (score >= 40) return 'Moderate Health';
    if (score >= 20) return 'Poor Health';
    return 'Critical Health';
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await api.post('/reports/generate', {
        type: 'organ_health',
        organHealthData
      });
      
      // Navigate to reports page with the new report
      navigate('/reports', { 
        state: { 
          newReport: response.data.report,
          message: 'Organ health report generated successfully!'
        }
      });
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated and no location state, show login prompt
  if (!isAuthenticated && !location.state?.results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-6xl mb-4">🔐</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
            <p className="text-gray-600 mb-6">
              Please login to view your organ health results, or complete the health assessment to see your predictions.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/questionnaire')}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Take Health Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Your Organ Health Results</h1>
          <p className="text-xl text-gray-600 mb-8">
            Comprehensive health assessment based on ML predictions
          </p>
          
          {latestAssessment && (
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl mx-auto mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Latest Assessment</h3>
              <p className="text-sm text-gray-600">
                Completed on {new Date(latestAssessment.assessment_date || Date.now()).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {/* Organ Health Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-12">
          {organHealthData.length > 0 ? (
            organHealthData.map((organ) => (
              <div key={organ.id} className={`border-2 rounded-xl p-6 transition-all duration-300 hover:shadow-xl ${getHealthColor(organ.health_score)}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{getOrganIcon(organ.organ_name)}</span>
                    <div>
                      <h3 className="text-xl font-bold">{organ.organ_name}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(organ.risk_level)}`}>
                        {organ.risk_level?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{organ.health_score}%</div>
                    <div className="text-sm font-medium">{getHealthMessage(organ.health_score)}</div>
                  </div>
                </div>

                {/* Recommendations */}
                {organ.recommendations && organ.recommendations.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold mb-2">Recommendations:</h4>
                    <ul className="space-y-1 text-sm">
                      {organ.recommendations.slice(0, 3).map((rec, index) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Next Assessment Date */}
                {organ.next_assessment_date && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Next assessment recommended: {new Date(organ.next_assessment_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-6xl mb-4">🏥</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Organ Health Data Available</h3>
              <p className="text-gray-600 mb-6">
                Complete the comprehensive health assessment to see your organ health results.
              </p>
              <button
                onClick={() => navigate('/questionnaire')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Take Health Assessment
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {organHealthData.length > 0 && (
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Back to Dashboard
            </button>
            
            <button
              onClick={generateReport}
              disabled={loading}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
            
            <button
              onClick={() => navigate('/questionnaire')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retake Assessment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganHealthResults;
